import React, { useState, useEffect } from 'react';
import { ArrowLeft, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import FutureDatePicker from './FutureDatePicker';

const API_URL = process.env.REACT_APP_API_URL;

function LimaDeliveryForm({ userId, cart, providerId, onBack, onSuccess }) {
  const [formData, setFormData] = useState({
    recipient_name: '',
    recipient_phone: '',
    address: '',
    district: '',
    charge: '',
    notes: '',
    delivery_date: ''
  });

  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDistricts();
  }, []);

  async function fetchDistricts() {
    try {
      const response = await axios.get(`${API_URL}/api/catalog/lima-districts`);
      setDistricts(response.data.districts);
    } catch (err) {
      console.error('Error fetching districts:', err);
      setError('Error al cargar distritos');
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }

  function handleDateChange(date) {
    setFormData(prev => ({
      ...prev,
      delivery_date: date
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Prepare items for the API
      const items = cart.map(item => ({
        sku: item.sku,
        quantity: item.quantity
      }));

      const payload = {
        user_id: userId,
        provider_id: providerId,
        recipient_name: formData.recipient_name,
        recipient_phone: formData.recipient_phone,
        address: formData.address,
        district: formData.district,
        charge: formData.charge || 0,
        additional_notes: formData.notes,
        delivery_date: formData.delivery_date,
        items: items
      };

      await axios.post(`${API_URL}/api/entrega`, payload);
      onSuccess();
    } catch (err) {
      console.error('Error creating delivery:', err);
      setError(err.response?.data?.error || 'Error al crear la entrega. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }

  const isFormValid = 
    formData.recipient_name.trim() &&
    formData.recipient_phone.trim() &&
    formData.address.trim() &&
    formData.district &&
    formData.delivery_date;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-2 text-brand-primary hover:text-brand-primary/80 transition-colors font-montserrat font-semibold mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Cambiar tipo de envío
      </button>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm font-montserrat">
          {error}
        </div>
      )}

      {/* Product Summary */}
      <div className="bg-slate-50 rounded-lg p-4">
        <h3 className="font-semibold text-slate-700 font-montserrat mb-2">Resumen del Pedido</h3>
        <div className="space-y-1">
          {cart.map(item => (
            <div key={item.sku} className="flex justify-between text-sm font-montserrat">
              <span className="text-slate-600">{item.quantity}x {item.title}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recipient Name */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
          Nombre del Destinatario *
        </label>
        <input
          type="text"
          name="recipient_name"
          value={formData.recipient_name}
          onChange={handleChange}
          required
          className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all font-montserrat"
          placeholder="Ej: Juan Pérez"
        />
      </div>

      {/* Recipient Phone */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
          WhatsApp del Destinatario *
        </label>
        <input
          type="tel"
          name="recipient_phone"
          value={formData.recipient_phone}
          onChange={handleChange}
          required
          className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all font-montserrat"
          placeholder="Ej: 987654321"
        />
      </div>

      {/* Delivery Date */}
      <FutureDatePicker
        selectedDate={formData.delivery_date}
        onDateChange={handleDateChange}
      />

      {/* Address */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
          Dirección *
        </label>
        <input
          type="text"
          name="address"
          value={formData.address}
          onChange={handleChange}
          required
          className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all font-montserrat"
          placeholder="Ej: Av. Principal 123, Dpto 401"
        />
      </div>

      {/* District */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
          Distrito *
        </label>
        <select
          name="district"
          value={formData.district}
          onChange={handleChange}
          required
          className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all font-montserrat"
        >
          <option value="">Selecciona un distrito</option>
          {districts.map(district => (
            <option key={district} value={district}>
              {district}
            </option>
          ))}
        </select>
      </div>

      {/* Charge */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
          Cargo (Monto a Cobrar)
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-montserrat text-sm">
            S/
          </span>
          <input
            type="number"
            name="charge"
            value={formData.charge}
            onChange={handleChange}
            step="0.01"
            min="0"
            className="w-full rounded-lg border-2 border-slate-300 bg-white pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all font-montserrat"
            placeholder="0.00"
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
          Notas Adicionales
        </label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all font-montserrat resize-none"
          placeholder="Información adicional sobre la entrega..."
        />
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!isFormValid || loading}
        className="w-full bg-brand-primary hover:bg-brand-primary/90 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-3 rounded-lg transition-all font-montserrat font-semibold flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Registrando...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            Registrar Envío
          </>
        )}
      </button>
    </form>
  );
}

export default LimaDeliveryForm;